import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDriverAuth } from '../context/DriverAuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Truck,
  LogOut,
  MapPin,
  Clock,
  Package,
  CheckCircle,
  FileText,
  Wallet,
  DollarSign,
  Calendar,
  Building2,
} from 'lucide-react';

import { getApiUrl, isBackendConfigured } from '../config';

const DriverDashboardPage = () => {
  const navigate = useNavigate();
  const { driver, token, loading, isAuthenticated, hasSignedAgreement, logout } = useDriverAuth();
  const [availableOrders, setAvailableOrders] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [balance, setBalance] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [bankingForm, setBankingForm] = useState({
    accountHolderName: '',
    bankName: '',
    routingNumber: '',
    accountNumber: '',
    accountType: 'checking',
  });
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [savingBanking, setSavingBanking] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/driver/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!loading && isAuthenticated && !hasSignedAgreement) {
      navigate('/driver/agreement');
    }
  }, [loading, isAuthenticated, hasSignedAgreement, navigate]);

  const loadDashboard = useCallback(async () => {
    if (!token) return;
    setFetching(true);
    setError('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [availableRes, mineRes, balanceRes] = await Promise.all([
        axios.get(getApiUrl('/api/drivers/orders/available'), { headers }),
        axios.get(getApiUrl('/api/drivers/orders/mine'), { headers }),
        axios.get(getApiUrl('/api/drivers/balance'), { headers }),
      ]);
      setAvailableOrders(availableRes.data);
      setMyOrders(mineRes.data);
      setBalance(balanceRes.data);

      if (balanceRes.data?.banking) {
        setBankingForm((prev) => ({
          ...prev,
          accountHolderName: balanceRes.data.banking.accountHolderName || prev.accountHolderName,
          bankName: balanceRes.data.banking.bankName || prev.bankName,
          accountType: balanceRes.data.banking.accountType || 'checking',
        }));
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setFetching(false);
    }
  }, [token]);

  useEffect(() => {
    if (token && hasSignedAgreement) {
      loadDashboard();
    }
  }, [token, hasSignedAgreement, loadDashboard]);

  const acceptOrder = async (orderId) => {
    setActionId(orderId);
    setError('');
    try {
      await axios.post(
        getApiUrl('/api/drivers/orders/${orderId}/accept'),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadDashboard();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to accept order');
    } finally {
      setActionId(null);
    }
  };

  const completeOrder = async (orderId) => {
    setActionId(orderId);
    setError('');
    try {
      await axios.patch(
        getApiUrl('/api/drivers/orders/${orderId}/complete'),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadDashboard();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to complete delivery');
    } finally {
      setActionId(null);
    }
  };

  const saveBanking = async (e) => {
    e.preventDefault();
    setSavingBanking(true);
    setError('');
    setSuccess('');
    try {
      await axios.post(getApiUrl('/api/drivers/banking'), bankingForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Banking details saved.');
      setBankingForm((prev) => ({ ...prev, routingNumber: '', accountNumber: '' }));
      await loadDashboard();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save banking details');
    } finally {
      setSavingBanking(false);
    }
  };

  const requestWithdrawal = async (e) => {
    e.preventDefault();
    setWithdrawing(true);
    setError('');
    setSuccess('');
    try {
      const payload = withdrawAmount ? { amount: parseFloat(withdrawAmount) } : {};
      const response = await axios.post(getApiUrl('/api/drivers/withdraw'), payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(response.data.message);
      setWithdrawAmount('');
      await loadDashboard();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to request withdrawal');
    } finally {
      setWithdrawing(false);
    }
  };

  const formatPayoutDate = (isoDate) => {
    if (!isoDate) return '';
    return new Date(`${isoDate}T12:00:00`).toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500';
      case 'processing':
        return 'bg-neutral-600';
      case 'completed':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'Awaiting driver';
      case 'processing':
        return 'Out for delivery';
      case 'completed':
        return 'Delivered';
      default:
        return status;
    }
  };

  const renderPayoutDetails = (payout) => {
    if (!payout) return null;
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-3 mb-4 text-sm text-green-900">
        <div className="flex items-center gap-2 font-semibold mb-1">
          <DollarSign className="w-4 h-4" />
          Driver payout: ${payout.totalPayout?.toFixed(2)}
        </div>
        <p className="text-green-800">
          {payout.deliveryMiles?.toFixed(1)} mi · ${payout.baseFee?.toFixed(0)} base
          {payout.mileageFee > 0
            ? ` + $${payout.mileageFee?.toFixed(2)} (${payout.additionalMiles?.toFixed(1)} mi after first)`
            : ' (first mile included)'}
        </p>
      </div>
    );
  };

  const OrderCard = ({ order, showAccept, showComplete }) => (
    <Card key={order.id} className="border-neutral-200">
      <CardContent className="pt-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <p className="font-bold text-neutral-900">{order.id}</p>
            <p className="text-sm text-gray-600">
              {order.items?.length || 0} item(s) · ${order.total?.toFixed(2)}
            </p>
          </div>
          <Badge className={`${getStatusColor(order.status)} text-white`}>
            {getStatusLabel(order.status)}
          </Badge>
        </div>

        <div className="space-y-2 text-sm text-neutral-700 mb-4">
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-neutral-500" />
            <span>{order.delivery?.address}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 shrink-0 text-neutral-500" />
            <span>
              {order.delivery?.date} · {order.delivery?.time}
            </span>
          </div>
          {order.delivery?.notes && (
            <p className="text-gray-600 pl-6">Note: {order.delivery.notes}</p>
          )}
        </div>

        <div className="rounded-lg bg-neutral-50 border p-3 mb-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">
            Order items
          </p>
          <ul className="space-y-1 text-sm">
            {order.items?.map((item) => (
              <li key={item.id} className="flex justify-between gap-2">
                <span>
                  {item.quantity}x {item.name}
                </span>
                <span className="text-neutral-600">${(item.price * item.quantity).toFixed(2)}</span>
              </li>
            ))}
          </ul>
        </div>

        {renderPayoutDetails(order.estimatedPayout)}

        <div className="bg-neutral-100 border border-neutral-200 rounded-lg p-3 mb-4 text-sm text-neutral-800">
          <strong>ID required at delivery.</strong> Verify government-issued photo ID and confirm
          the recipient is 21+ before completing handoff.
        </div>

        {showAccept && (
          <Button
            className="w-full bg-neutral-950 hover:bg-black"
            disabled={actionId === order.id}
            onClick={() => acceptOrder(order.id)}
          >
            {actionId === order.id ? 'Accepting...' : 'Accept delivery'}
          </Button>
        )}

        {showComplete && order.status === 'processing' && (
          <Button
            className="w-full bg-green-600 hover:bg-green-700"
            disabled={actionId === order.id}
            onClick={() => completeOrder(order.id)}
          >
            {actionId === order.id ? 'Updating...' : 'Mark delivered'}
          </Button>
        )}
      </CardContent>
    </Card>
  );

  if (loading || !isAuthenticated || !hasSignedAgreement) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-neutral-950 rounded-full">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">Driver Dashboard</h1>
              <p className="text-sm text-gray-600">Welcome, {driver?.name}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/driver/agreement')}>
              <FileText className="w-4 h-4 mr-2" />
              View agreement
            </Button>
            <Button variant="outline" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Log out
            </Button>
          </div>
        </div>

        {driver?.agreementSignedAt && (
          <div className="mb-6 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            <CheckCircle className="w-4 h-4 shrink-0" />
            Agreement signed on {new Date(driver.agreementSignedAt).toLocaleDateString()}
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
            {success}
          </div>
        )}

        {balance && (
          <div className="mb-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
              <Card className="border-neutral-200">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                    <Wallet className="w-4 h-4" />
                    Withdrawable balance
                  </div>
                  <p className="text-3xl font-bold text-neutral-900">
                    ${balance.withdrawableBalance?.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Ready to request payout</p>
                </CardContent>
              </Card>
              <Card className="border-neutral-200">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                    <Truck className="w-4 h-4" />
                    Pending earnings
                  </div>
                  <p className="text-3xl font-bold text-neutral-900">
                    ${balance.pendingBalance?.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {balance.activeDeliveries} active deliver
                    {balance.activeDeliveries === 1 ? 'y' : 'ies'}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-neutral-200">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                    <Calendar className="w-4 h-4" />
                    Scheduled payout
                  </div>
                  <p className="text-3xl font-bold text-neutral-900">
                    ${balance.scheduledPayoutAmount?.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Next: {formatPayoutDate(balance.nextPayoutDate)}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-neutral-200 bg-neutral-950 text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 text-sm text-neutral-300 mb-1">
                    <DollarSign className="w-4 h-4" />
                    Total earned
                  </div>
                  <p className="text-3xl font-bold">${balance.earnedBalance?.toFixed(2)}</p>
                  <p className="text-xs text-neutral-400 mt-1">{balance.rateDescription}</p>
                </CardContent>
              </Card>
            </div>
            <p className="text-sm text-gray-600">{balance.payoutScheduleDescription}</p>
          </div>
        )}

        <Tabs defaultValue="available" className="w-full">
          <TabsList className="grid w-full max-w-2xl grid-cols-3 mb-6">
            <TabsTrigger value="available">
              Available ({availableOrders.length})
            </TabsTrigger>
            <TabsTrigger value="mine">My deliveries ({myOrders.length})</TabsTrigger>
            <TabsTrigger value="earnings">Earnings</TabsTrigger>
          </TabsList>

          <TabsContent value="available">
            {fetching ? (
              <p className="text-gray-600">Loading orders...</p>
            ) : availableOrders.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-600">
                  <Package className="w-12 h-12 mx-auto mb-3 text-neutral-400" />
                  No open deliveries right now. Check back soon.
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {availableOrders.map((order) => (
                  <OrderCard key={order.id} order={order} showAccept />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="mine">
            {fetching ? (
              <p className="text-gray-600">Loading orders...</p>
            ) : myOrders.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-600">
                  You have not accepted any deliveries yet.
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {myOrders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    showComplete
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="earnings">
            {fetching ? (
              <p className="text-gray-600">Loading earnings...</p>
            ) : (
              <div className="space-y-6">
                <div className="grid gap-6 lg:grid-cols-2">
                  <Card className="border-neutral-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Building2 className="w-5 h-5 text-neutral-700" />
                        <h3 className="font-semibold text-neutral-900">Banking details</h3>
                      </div>
                      {balance?.hasBankingOnFile && balance?.banking && (
                        <div className="mb-4 rounded-lg bg-neutral-50 border p-3 text-sm text-neutral-700">
                          <p>
                            {balance.banking.bankName} · {balance.banking.accountType}
                          </p>
                          <p>
                            {balance.banking.accountHolderName} · ****
                            {balance.banking.accountNumberLast4}
                          </p>
                        </div>
                      )}
                      <form onSubmit={saveBanking} className="space-y-3">
                        <div>
                          <Label htmlFor="bank-holder">Account holder name</Label>
                          <Input
                            id="bank-holder"
                            value={bankingForm.accountHolderName}
                            onChange={(e) =>
                              setBankingForm({ ...bankingForm, accountHolderName: e.target.value })
                            }
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="bank-name">Bank name</Label>
                          <Input
                            id="bank-name"
                            value={bankingForm.bankName}
                            onChange={(e) =>
                              setBankingForm({ ...bankingForm, bankName: e.target.value })
                            }
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="routing">Routing number</Label>
                          <Input
                            id="routing"
                            inputMode="numeric"
                            value={bankingForm.routingNumber}
                            onChange={(e) =>
                              setBankingForm({ ...bankingForm, routingNumber: e.target.value })
                            }
                            placeholder={balance?.hasBankingOnFile ? 'Enter to update' : ''}
                            required={!balance?.hasBankingOnFile}
                          />
                        </div>
                        <div>
                          <Label htmlFor="account">Account number</Label>
                          <Input
                            id="account"
                            inputMode="numeric"
                            value={bankingForm.accountNumber}
                            onChange={(e) =>
                              setBankingForm({ ...bankingForm, accountNumber: e.target.value })
                            }
                            placeholder={balance?.hasBankingOnFile ? 'Enter to update' : ''}
                            required={!balance?.hasBankingOnFile}
                          />
                        </div>
                        <div>
                          <Label htmlFor="account-type">Account type</Label>
                          <select
                            id="account-type"
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                            value={bankingForm.accountType}
                            onChange={(e) =>
                              setBankingForm({ ...bankingForm, accountType: e.target.value })
                            }
                          >
                            <option value="checking">Checking</option>
                            <option value="savings">Savings</option>
                          </select>
                        </div>
                        <Button
                          type="submit"
                          className="w-full bg-neutral-950 hover:bg-black"
                          disabled={savingBanking}
                        >
                          {savingBanking ? 'Saving...' : 'Save banking details'}
                        </Button>
                      </form>
                    </CardContent>
                  </Card>

                  <Card className="border-neutral-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Wallet className="w-5 h-5 text-neutral-700" />
                        <h3 className="font-semibold text-neutral-900">Request withdrawal</h3>
                      </div>
                      <p className="text-sm text-gray-600 mb-4">
                        Withdrawals are paid every Friday. Your first payout is scheduled for the
                        first Friday at least one week after you start driving (
                        {formatPayoutDate(balance?.firstPayoutDate)}).
                      </p>
                      <form onSubmit={requestWithdrawal} className="space-y-3">
                        <div>
                          <Label htmlFor="withdraw-amount">Amount (optional)</Label>
                          <Input
                            id="withdraw-amount"
                            type="number"
                            min="0.01"
                            step="0.01"
                            max={balance?.withdrawableBalance}
                            placeholder={`Full balance ($${balance?.withdrawableBalance?.toFixed(2) || '0.00'})`}
                            value={withdrawAmount}
                            onChange={(e) => setWithdrawAmount(e.target.value)}
                          />
                        </div>
                        <Button
                          type="submit"
                          className="w-full bg-green-600 hover:bg-green-700"
                          disabled={
                            withdrawing ||
                            !balance?.hasBankingOnFile ||
                            (balance?.withdrawableBalance || 0) <= 0
                          }
                        >
                          {withdrawing
                            ? 'Scheduling...'
                            : `Withdraw up to $${balance?.withdrawableBalance?.toFixed(2) || '0.00'}`}
                        </Button>
                        {!balance?.hasBankingOnFile && (
                          <p className="text-xs text-amber-700">
                            Add banking details before requesting a withdrawal.
                          </p>
                        )}
                      </form>

                      {balance?.withdrawalHistory?.length > 0 && (
                        <div className="mt-6">
                          <p className="text-sm font-semibold text-neutral-900 mb-2">
                            Withdrawal history
                          </p>
                          <ul className="space-y-2">
                            {balance.withdrawalHistory.map((entry) => (
                              <li
                                key={entry.id}
                                className="flex justify-between gap-2 text-sm border rounded-lg p-3"
                              >
                                <div>
                                  <p className="font-medium">${entry.amount?.toFixed(2)}</p>
                                  <p className="text-gray-500 capitalize">{entry.status}</p>
                                </div>
                                <div className="text-right text-gray-600">
                                  {entry.scheduledPayoutDate && (
                                    <p>{formatPayoutDate(entry.scheduledPayoutDate)}</p>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {!balance?.payoutHistory?.length ? (
                  <Card>
                    <CardContent className="py-12 text-center text-gray-600">
                      Complete deliveries to start earning. Rate: {balance?.rateDescription}
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-neutral-900">Delivery earnings</h3>
                    {balance.payoutHistory.map((entry) => (
                      <Card key={entry.orderId} className="border-neutral-200">
                        <CardContent className="pt-6 flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="font-semibold text-neutral-900">{entry.orderId}</p>
                            <p className="text-sm text-gray-600">{entry.deliveryAddress}</p>
                            {entry.completedAt && (
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(entry.completedAt).toLocaleString()}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-green-700">
                              +${entry.payout?.totalPayout?.toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {entry.payout?.deliveryMiles?.toFixed(1)} mi · $
                              {entry.payout?.baseFee?.toFixed(0)} + $
                              {entry.payout?.mileageFee?.toFixed(2)} mileage
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <div className="mt-6 text-center">
          <Button variant="ghost" onClick={loadDashboard} disabled={fetching}>
            Refresh dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DriverDashboardPage;
